import * as React from 'react';
import {
    AdvancedTable, Card, DefaultMultiSelectSearch, MultiSelectSearch, WarningModal,
} from 'components';
import { useEffectProcessing, useMultiSearch } from 'hooks';
import { IExpertsFilters, IQualificationsFilters } from 'components/reduxModels';
import { ExpertStatusOptions, MappedExpertStatus } from 'components/enums';
import {
    formatFIO, formatPhoneNumber, formatQualification, formatSnils,
} from '_helpers';
import {
    IExpert, IExpertDetails, IExperts, IQualificationInfoList,
} from '../models';

const Experts: React.FC<IProps> = (props) => {
    const isAsc = !!props.ascId;
    const { ascId } = props;
    const { filters, changeFilterField, changeFilter } = useMultiSearch<IExpertsFilters>(props.getExperts, {});
    const [selectedId, selectId] = React.useState<string | null>(null);
    const processing = useEffectProcessing(
      async () => {
          await props.getExperts(filters);
          if (props.getQualifications && !isAsc) await props.getQualifications({ pageSize: 500 });
      },
      [],
      () => {
          props.clearExperts();
          if (props.clearQualifications && !isAsc) props.clearQualifications();
      },
    );

    const getStatus = (e: IExpert, id?: string) => {
        if (!id) return '';
        const linked = e.linkedExpertStatuses.find(s => s.ascId === id);
        if (linked) return linked.status;

        return '';
    };

    const data = props.experts ? props.experts.items.map(e => ({
        id: e.id,
        columns: isAsc ? [
            formatFIO(e),
            MappedExpertStatus[getStatus(e, ascId)].text,
            formatSnils(e.snils),
            formatPhoneNumber(e.phone) || e.phone,
        ] : [
            formatFIO(e),
            formatSnils(e.snils),
            formatPhoneNumber(e.phone) || e.phone,
        ],
    })) : [];

    const header = isAsc ? [
        { fieldName: 'fullName', title: 'ИМЯ, ФАМИЛИЯ' },
        {
            fieldName: 'status', title: 'СТАТУС', columnStyles: { width: 215 }, selectOptions: ExpertStatusOptions,
        },
        { fieldName: 'snils', title: 'СНИЛС', columnStyles: { width: 155 } },
        { fieldName: 'phone', title: 'ТЕЛЕФОН', columnStyles: { width: 165 } },
    ] : [
        { fieldName: 'fullName', title: 'ИМЯ, ФАМИЛИЯ' },
        { fieldName: 'snils', title: 'СНИЛС', columnStyles: { width: 155 } },
        { fieldName: 'phone', title: 'ТЕЛЕФОН', columnStyles: { width: 165 } },
    ];

    const title = isAsc ? 'Члены АПК' : 'Список членов АК';
    const category = isAsc ? 'Список членов, входящих в состав АПК' : 'Список всех членов аккредитационной комиссии';

    const options = Object.keys(MappedExpertStatus).map(status => ({
        value: status,
        label: MappedExpertStatus[status].text,
    }));

    options.unshift({value: '', label: 'Все'});

    return (
      <Card
        title={title}
        category={category}
        withoutPadding
        showLoader={processing}
      >
          <MultiSelectSearch hide={isAsc}>
              <DefaultMultiSelectSearch
                label="СТАТУС"
                options={options}
                placeholder="Выберите один или несколько статусов членов комиссий"
                onChange={changeFilterField('statuses')}
              />
              <DefaultMultiSelectSearch
                label="СПЕЦИАЛЬНОСТЬ"
                options={props.qualifications ? props.qualifications.items.map(q => ({
                    label: formatQualification(q),
                    value: q.id,
                })) : []}
                placeholder="Выберите одну или несколько специальностей"
                onChange={changeFilterField('qualificationIds')}
              />
          </MultiSelectSearch>

          <AdvancedTable
            data={data}
            tableHeaders={header}
            handleRowClick={selectId}
            handleSearch={(f, t) => changeFilter({
                searchTerm: t,
                searchField: f,
            })}
            handleOrder={f => changeFilter({
                sortField: f,
                sortOrder: f === filters.sortField ? !filters.sortOrder : true,
            })}
            handlePageChange={p => changeFilter({
                pageSize: p.perPage,
                page: p.page,
            })}
          />

          {!!props.renderDetails && (
            <WarningModal
              large
              card
              withoutCancelButton
              withoutTitleMargin
              title={props.expertDetails ? formatFIO(props.expertDetails) : ''}
              show={!!selectedId}
              onHide={() => {
                  selectId(null);
              }}
            >
                {!!selectedId && props.renderDetails(selectedId)}
            </WarningModal>
          )}

      </Card>
    );
};

interface IProps {
    ascId?: string;
    qualifications?: IQualificationInfoList | null;
    experts: IExperts | null;
    expertDetails: IExpertDetails | null;

    getQualifications?: (payload: IQualificationsFilters) => Promise<void>;
    clearQualifications?: () => void;
    getExperts: (payload: IExpertsFilters) => Promise<void>;
    clearExperts: () => void;

    renderDetails?: (id: string) => React.ReactNode;
}

export default Experts;
